import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  callsMade,
  CONSOLIDATE_REFINEMENT,
  CONSOLIDATE_SETS,
  consolidateSetLabels,
  consolidateSummary,
  consolidateTransition,
  COUNT_QUESTION,
  DESCRIPTION_REFINEMENT,
  descriptionRuns,
  descriptionSummary,
  descriptionTransition,
  ENTRIES,
  entryJson,
  entryLabels,
  formatTokens,
  initialConsolidate,
  initialDescription,
  initialMcp,
  initialResults,
  lastCallTokens,
  MCP_ANSWERED_BY,
  MCP_REFINEMENT,
  MCP_REQUESTS,
  mcpExchanges,
  mcpRequestLabels,
  mcpSummary,
  mcpTransition,
  RESULT_SHAPES,
  resultAnswers,
  resultShapeLabels,
  RESULTS_REFINEMENT,
  resultsSummary,
  resultsTransition,
  resultTraces,
  RIGHT_ANSWER_LINE,
  toConsolidateSet,
  toEntry,
  toMcpRequest,
  toolNames,
  toResultShape,
  type ConsolidateAction,
  type DescriptionAction,
  type McpAction,
  type ResultsAction,
} from './tooldesign-models';

import './relationship-labs.css';
import './tooldesign-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const bullets = (names: readonly string[]) => names.map((name) => `- ${name}`).join('\n');
const REPLAY = 'This graphic replays real runs.';

export function ToolDesignDescriptionLab() {
  const [state, setState] = useState(initialDescription);
  const act = (action: DescriptionAction) =>
    setState((current) => descriptionTransition(current, action));
  const run = descriptionRuns[state.entry];
  const summary = descriptionSummary(state);
  return (
    <Lab
      title="What does the model know about a tool?"
      eyebrow="Tool design · the description"
      className="rel-lab tooldesign-lab"
    >
      <Predict>
        What does the model know about a tool? Choose an entry, then press Ask what is open in
        Support.
      </Predict>
      <div className="cb-controls">
        <label>
          Entry
          <ModelSelect
            aria-label="Entry"
            value={state.entry}
            onValueChange={(value) => act({ type: 'entry', value: toEntry(value) })}
            options={ENTRIES.map((entry) => ({ value: entry, label: entryLabels[entry] }))}
          />
        </label>
        <Button size="sm" disabled={state.asked} onClick={() => act({ type: 'ask' })}>
          Ask what is open in Support
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The entry as the model sees it">
        <pre aria-label="The entry as the model sees it">{entryJson[state.entry]}</pre>
      </Panel>
      {state.asked ? (
        <Facts
          values={[
            ['Prompt tokens', String(run.promptTokens)],
            ['First list_tasks call', run.firstCall],
            ['Answer', run.answer],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{DESCRIPTION_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ToolDesignConsolidateLab() {
  const [state, setState] = useState(initialConsolidate);
  const act = (action: ConsolidateAction) =>
    setState((current) => consolidateTransition(current, action));
  const run = state.run;
  const summary = consolidateSummary(state);
  return (
    <Lab
      title="How many tokens does a tool set cost, and does the model ask when it cannot choose?"
      eyebrow="Tool design · fewer, clearer tools"
      className="rel-lab tooldesign-lab"
    >
      <Predict>
        Does the model ask which task is meant? Choose a tool set, then press Ask who owns the
        release note task.
      </Predict>
      <div className="cb-controls">
        <label>
          Tool set
          <ModelSelect
            aria-label="Tool set"
            value={state.set}
            onValueChange={(value) => act({ type: 'set', value: toConsolidateSet(value) })}
            options={CONSOLIDATE_SETS.map((set) => ({
              value: set,
              label: consolidateSetLabels[set],
            }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.askOn}
          onClick={() => act({ type: 'toggle-ask' })}
        >
          Tell the model to ask when a request is ambiguous
        </Button>
        <Button size="sm" disabled={run !== null} onClick={() => act({ type: 'ask' })}>
          Ask who owns the release note task
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="cb-two">
        <Panel label="The tool names">
          <pre aria-label="The tool names">{bullets(toolNames[state.set])}</pre>
        </Panel>
        <Panel label="The answer">
          <pre aria-label="The answer">
            {run ? (run.answer ?? '(not measured for this set)') : '(not asked yet)'}
          </pre>
        </Panel>
      </div>
      {run ? (
        <Facts
          values={[
            ['Prompt tokens, first call', String(run.promptTokens)],
            ['Tools sent', String(run.toolsSent)],
            ['What the model did', run.did],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{CONSOLIDATE_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ToolDesignResultsLab() {
  const [state, setState] = useState(initialResults);
  const act = (action: ResultsAction) => setState((current) => resultsTransition(current, action));
  const summary = resultsSummary(state);
  return (
    <Lab
      title="What does a large result cost, and who should count?"
      eyebrow="Tool design · the result shape"
      className="rel-lab tooldesign-lab"
    >
      <Predict>
        Who should count the done tasks? Choose a result shape, then press Ask how many are done.
      </Predict>
      <div className="cb-controls">
        <label>
          Result shape
          <ModelSelect
            aria-label="Result shape"
            value={state.shape}
            onValueChange={(value) => act({ type: 'shape', value: toResultShape(value) })}
            options={RESULT_SHAPES.map((shape) => ({
              value: shape,
              label: resultShapeLabels[shape],
            }))}
          />
        </label>
        <Button size="sm" disabled={state.asked} onClick={() => act({ type: 'ask' })}>
          Ask how many are done
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The question">
        <pre aria-label="The question">{COUNT_QUESTION}</pre>
      </Panel>
      <Table
        label="Calls"
        columns={['Call', 'The model asks for', 'Our code sends back']}
        template="minmax(0, 0.4fr) minmax(0, 2fr) minmax(0, 1.2fr)"
        rows={
          state.asked
            ? resultTraces[state.shape].map((entry, index) => [index + 1, entry.call, entry.result])
            : []
        }
      />
      {state.asked ? (
        <>
          <Facts
            values={[
              ['Calls made', String(callsMade(state.shape))],
              ['Prompt tokens, last call', formatTokens(lastCallTokens(state.shape))],
              ['Answer', resultAnswers[state.shape]],
            ]}
          />
          <p className="rel-caption">{RIGHT_ANSWER_LINE}</p>
        </>
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{RESULTS_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ToolDesignMcpLab() {
  const [state, setState] = useState(initialMcp);
  const act = (action: McpAction) => setState((current) => mcpTransition(current, action));
  const exchange = mcpExchanges[state.request];
  const summary = mcpSummary(state);
  return (
    <Lab
      title="What does an MCP client send and receive?"
      eyebrow="Tool design · the same tools over MCP"
      className="rel-lab tooldesign-lab"
    >
      <Predict>What does the server send back? Choose a request, then press Send.</Predict>
      <div className="cb-controls">
        <label>
          Request
          <ModelSelect
            aria-label="Request"
            value={state.request}
            onValueChange={(value) => act({ type: 'request', value: toMcpRequest(value) })}
            options={MCP_REQUESTS.map((request) => ({
              value: request,
              label: mcpRequestLabels[request],
            }))}
          />
        </label>
        <Button size="sm" disabled={state.sent} onClick={() => act({ type: 'send' })}>
          Send
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The client sends">
        <pre aria-label="The client sends">{exchange.request}</pre>
      </Panel>
      <Panel label="The server answers">
        <pre aria-label="The server answers">
          {state.sent ? exchange.response : '(not sent yet)'}
        </pre>
      </Panel>
      {state.sent ? (
        <Facts
          values={[
            ['Method', exchange.method],
            ['Who answers', MCP_ANSWERED_BY],
            ['What the client learns', exchange.learns],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{MCP_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
